import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { lessonApi } from '@/lib/api-client';
import { queryKeys } from '@/config/query-client';
import { parseMindmapMarkdown, type MindmapNode } from '@/lib/mindmap-markdown';
import { MindmapCanvas } from '../../media/mindmap/MindmapCanvas';

/** How often the draft map is re-read while the concepts are written up. */
const DRAFT_POLL_MS = 3000;

/**
 * The lesson's mind map as it stands - the plan's draft outline while
 * `byte_selection` writes the concepts' details (`stage: 'details'`, the endpoint answers
 * `status: 'processing'`), then the final map. Same query key as the Mindmap
 * tab, so the tab opens on a warm cache when the build finishes.
 */
export function useDraftOutline(
  lessonId: string | undefined,
  { enabled, polling }: { enabled: boolean; polling: boolean }
): MindmapNode | null {
  const { data } = useQuery({
    queryKey: queryKeys.lessons.mindmap(lessonId ?? ''),
    queryFn: () => lessonApi.getLessonMindmap(lessonId as string),
    enabled: enabled && !!lessonId,
    refetchInterval: polling ? DRAFT_POLL_MS : false,
    staleTime: 0,
  });
  const markdown = data?.markdown_content;
  return useMemo(
    () => (markdown ? parseMindmapMarkdown(markdown) : null),
    [markdown]
  );
}

/**
 * "Your outline so far": the draft outline drawn by the lesson's own mind map
 * (`MindmapCanvas` in `preview` mode) - the same pills, colours and layout the
 * finished map opens with, fitted whole into a card as tall as the screen
 * leaves room for: lesson, sections and (when readable) concepts drawn, the
 * next level only as lines that fade out, and nothing to pan, zoom or click.
 */
export function BuildOutline({ tree }: { tree: MindmapNode }) {
  return (
    <section
      aria-labelledby='build-outline-title'
      className='flex h-[clamp(16rem,calc(100dvh-27rem),38rem)] w-full flex-col overflow-hidden rounded-xl border border-border bg-card text-left shadow-sm'
    >
      <h3
        id='build-outline-title'
        className='shrink-0 px-5 pt-4 font-mono text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground'
      >
        Your outline so far
      </h3>
      <div className='min-h-0 w-full flex-1'>
        <MindmapCanvas
          tree={tree}
          mode='inline'
          preview
          label='Your outline so far'
        />
      </div>
    </section>
  );
}
